import {NeoDatePicker, NeoSelect} from "@/components/forms";
import {EyeIcon} from "@/components/shared";
import {NeoDashboardService} from "@/service/dashboard.service";
import {SelectItem} from "@nextui-org/react";
import dynamic from "next/dynamic";
import {useRouter} from "next/navigation";
import {useEffect, useState} from "react";
import Loader from "../Loader";
const Chart = dynamic(() => import("react-apexcharts"), {ssr: false});
const expensecFilter = [
  {
    value: "last30Days",
    label: "Last 30 Days"
  },
  {
    value: "currentMonth",
    label: "Current Month"
  },
  {
    value: "lastMonth",
    label: "Last Month"
  },
  {
    value: "custom",
    label: "Custom"
  }
];

interface ExpenceSummaryProps {
  isLoading: boolean;
  expenseSummaryDatas: any;
  instituteuserids: any;
}

const ExpenseSummaryWidget: React.FC<ExpenceSummaryProps> = ({
  isLoading,
  expenseSummaryDatas,
  instituteuserids
}) => {
  const [expenseSummarySeries, setexpenseSummarySeries] = useState<number[]>(
    []
  );
  const [customLoader, setCustomLoader] = useState(false);
  const [expenseRange, setExpenseRange] = useState<
    "last30Days" | "currentMonth" | "lastMonth" | "custom"
  >("currentMonth");
  const [expenseSummaryChartKey, setexpenseSummaryChartKey] =
    useState<number>(0);
  const expenseSummary_Options: any = {
    labels: ["Fuel", "Maintenance", "Miscellaneous"],
    colors: ["#FF9F0A", "#0067FF", "#349C42"],
    dataLabels: {
      enabled: false,
      formatter: (
        val: number,
        {seriesIndex, w}: {seriesIndex: number; w: any}
      ) => w.config.series[seriesIndex]
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return `${val.toFixed(2)}%`; // Shows value with two decimal places and percentage symbol
        }
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: "75%",
          labels: {
            show: false,
            value: {
              show: false,
              fontSize: "14px",
              fontWeight: 600,
              offsetY: -5
            }
          }
        }
      }
    },
    legend: {
      show: false,
      position: "bottom",
      horizontalAlign: "center",
      formatter: function (seriesName: any, {seriesIndex}: any) {
        var value = expenseSummarySeries[seriesIndex];
        return `${seriesName}: ${value}`;
      },
      onItemClick: {
        toggleDataSeries: false
      }
    },
    responsive: [
      {
        breakpoint: 250,
        options: {
          chart: {
            width: 250
          }
        }
      }
    ]
  };
  const [expenseSummaryOptions, setexpenseSummaryOptions] = useState(
    expenseSummary_Options
  );

  const [selectedData, setSelectedData] = useState({
    total: 0,
    fuel: 0,
    maintenance: 0,
    miscellaneous: 0
  });

  const [timeRange, setTimeRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: null,
    to: null
  });

  const [showDatePicker, SetShowDatePicker] = useState(false);

  // expense summary percentage
  function expenseFindPercent(expenseSummary: any, expenseRange: any) {
    const selected = expenseSummary?.[expenseRange];
    const fuel = selected.fuel || 0;
    const maintenance = selected.maintenance || 0;
    const miscellaneous = selected.miscellaneous || 0;

    const total = fuel + maintenance + miscellaneous;
    if (total === 0) return [0, 0, 0];

    return [
      Number(((fuel / total) * 100).toFixed(2)),
      Number(((maintenance / total) * 100).toFixed(2)),
      Number(((miscellaneous / total) * 100).toFixed(2))
    ];
  }

  // date picker

  const onDateChange = (value: any) => {
    setTimeRange({
      from: value?.from ?? null,
      to: value?.to ?? null
    });
  };

  useEffect(() => {
    const run = async () => {
      if (expenseRange === "custom") {
        SetShowDatePicker(true);
      } else {
        SetShowDatePicker(false);
      }
      if (expenseRange === "custom" || expenseRange === "lastMonth") {
        const currentDate = new Date();
        let fromdate;
        let todate;
        if (expenseRange === "lastMonth") {
          const lastMonthFrom = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() - 1,
            1
          );
          const lastMonthTo = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            0
          );
          lastMonthTo.setHours(23, 59, 59, 999);
          fromdate = lastMonthFrom.getTime();
          todate = lastMonthTo.getTime();
        }
        if (expenseRange === "custom") {
          if (!timeRange?.from || !timeRange?.to) return;

          const customFrom = new Date(timeRange.from);
          const customTo = new Date(timeRange.to);
          customTo.setHours(0, 0, 0, 999);

          fromdate = customFrom.getTime();
          todate = customTo.getTime();
        }
        const customRequestData = {
          from: fromdate,
          to: todate,
          type: 1,
          filters: {branch: instituteuserids}
        };
        setCustomLoader(true);
        const customresponse =
          await NeoDashboardService.onCustomExpenseData(customRequestData);

        if (customresponse?.data?.status !== "success") {
          return;
        }

        const summary = customresponse?.data?.data?.reportData?.summary;

        const convertdata = {
          [expenseRange]: {
            total: summary?.totalExpense ?? 0,
            fuel: summary?.fuelExpense ?? 0,
            maintenance: summary?.maintenanceExpense ?? 0,
            miscellaneous: summary?.miscExpense ?? 0
          }
        };

        const series = expenseFindPercent(convertdata, expenseRange);

        setSelectedData(convertdata[expenseRange]);
        setexpenseSummarySeries(series);

        setexpenseSummaryChartKey(prev => prev + 1);
        setCustomLoader(false);
        return;
      } else {
        const data = expenseSummaryDatas?.[expenseRange] ?? {
          total: 0,
          fuel: 0,
          maintenance: 0,
          miscellaneous: 0
        };

        setSelectedData(data);

        const series = expenseFindPercent(expenseSummaryDatas, expenseRange);

        setexpenseSummarySeries(series);
        setexpenseSummaryChartKey(prev => prev + 1);
      }
    };
    run();
  }, [expenseSummaryDatas, expenseRange, timeRange]);

  const [fuelpercent, maintenancepercent, miscpercent] =
    (expenseSummarySeries ?? [0, 0, 0]) as number[];

  const {push} = useRouter();

  const redirectExpenseReport = () => {
    push(`/expensereports`);
  };
  return (
    <>
      {/* main */}
      <div className="rounded-xl border bg-neo-white">
        <div className=" grid grid-flow-row gap-1 px-2">
          {/* head */}
          <div className="flex items-center justify-between p-2">
            <h5 className="pl-1 text-xl font-semibold">Expense Summary</h5>
            <h5
              className="cursor-pointer pr-2"
              title="View"
              onClick={redirectExpenseReport}
            >
              <EyeIcon size={18} />
            </h5>
          </div>
          {/* head e */}

          {isLoading || customLoader ? (
            <Loader />
          ) : (
            <>
              {/* 2 col s */}
              {/* flex items-center justify-between gap-3 rounded-2xl border  px-1 py-2 */}
              <div className="flex items-center justify-between gap-3 rounded-2xl border  px-1 py-2">
                {/* NeoSelect */}
                <NeoSelect
                  size="sm"
                  selectedKeys={[expenseRange]}
                  startContent={
                    <svg
                      width="15"
                      height="16"
                      viewBox="0 0 21 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18.5725 1.68841H16.0399V0.844207C16.0399 0.620309 15.951 0.405582 15.7927 0.247262C15.6343 0.0889429 15.4196 0 15.1957 0C14.9718 0 14.7571 0.0889429 14.5988 0.247262C14.4405 0.405582 14.3515 0.620309 14.3515 0.844207V1.68841H5.90945V0.844207C5.90945 0.620309 5.8205 0.405582 5.66219 0.247262C5.50387 0.0889429 5.28914 0 5.06524 0C4.84134 0 4.62662 0.0889429 4.4683 0.247262C4.30998 0.405582 4.22103 0.620309 4.22103 0.844207V1.68841H1.68841C1.24062 1.68841 0.811164 1.8663 0.494525 2.18294C0.177886 2.49958 0 2.92903 0 3.37683V20.261C0 20.7088 0.177886 21.1382 0.494525 21.4549C0.811164 21.7715 1.24062 21.9494 1.68841 21.9494H18.5725C19.0203 21.9494 19.4498 21.7715 19.7664 21.4549C20.0831 21.1382 20.261 20.7088 20.261 20.261V3.37683C20.261 2.92903 20.0831 2.49958 19.7664 2.18294C19.4498 1.8663 19.0203 1.68841 18.5725 1.68841ZM5.48734 17.7283C5.23689 17.7283 4.99206 17.6541 4.78382 17.5149C4.57558 17.3758 4.41327 17.178 4.31743 16.9466C4.22158 16.7152 4.19651 16.4606 4.24537 16.215C4.29423 15.9693 4.41483 15.7437 4.59193 15.5666C4.76902 15.3895 4.99466 15.2689 5.2403 15.2201C5.48594 15.1712 5.74055 15.1963 5.97194 15.2921C6.20333 15.388 6.4011 15.5503 6.54024 15.7585C6.67939 15.9668 6.75365 16.2116 6.75365 16.462C6.75365 16.7979 6.62024 17.12 6.38276 17.3574C6.14528 17.5949 5.82319 17.7283 5.48734 17.7283ZM10.1305 17.7283C9.88003 17.7283 9.6352 17.6541 9.42696 17.5149C9.21871 17.3758 9.05641 17.178 8.96056 16.9466C8.86472 16.7152 8.83964 16.4606 8.8885 16.215C8.93736 15.9693 9.05797 15.7437 9.23507 15.5666C9.41216 15.3895 9.6378 15.2689 9.88344 15.2201C10.1291 15.1712 10.3837 15.1963 10.6151 15.2921C10.8465 15.388 11.0442 15.5503 11.1834 15.7585C11.3225 15.9668 11.3968 16.2116 11.3968 16.462C11.3968 16.7979 11.2634 17.12 11.0259 17.3574C10.7884 17.5949 10.4663 17.7283 10.1305 17.7283ZM10.1305 13.5073C9.88003 13.5073 9.6352 13.433 9.42696 13.2939C9.21871 13.1548 9.05641 12.957 8.96056 12.7256C8.86472 12.4942 8.83964 12.2396 8.8885 11.994C8.93736 11.7483 9.05797 11.5227 9.23507 11.3456C9.41216 11.1685 9.6378 11.0479 9.88344 10.999C10.1291 10.9502 10.3837 10.9752 10.6151 11.0711C10.8465 11.1669 11.0442 11.3292 11.1834 11.5375C11.3225 11.7457 11.3968 11.9905 11.3968 12.241C11.3968 12.5768 11.2634 12.8989 11.0259 13.1364C10.7884 13.3739 10.4663 13.5073 10.1305 13.5073ZM14.7736 17.7283C14.5232 17.7283 14.2783 17.6541 14.0701 17.5149C13.8619 17.3758 13.6995 17.178 13.6037 16.9466C13.5079 16.7152 13.4828 16.4606 13.5316 16.215C13.5805 15.9693 13.7011 15.7437 13.8782 15.5666C14.0553 15.3895 14.2809 15.2689 14.5266 15.2201C14.7722 15.1712 15.0268 15.1963 15.2582 15.2921C15.4896 15.388 15.6874 15.5503 15.8265 15.7585C15.9657 15.9668 16.0399 16.2116 16.0399 16.462C16.0399 16.7979 15.9065 17.12 15.669 17.3574C15.4316 17.5949 15.1095 17.7283 14.7736 17.7283ZM14.7736 13.5073C14.5232 13.5073 14.2783 13.433 14.0701 13.2939C13.8619 13.1548 13.6995 12.957 13.6037 12.7256C13.5079 12.4942 13.4828 12.2396 13.5316 11.994C13.5805 11.7483 13.7011 11.5227 13.8782 11.3456C14.0553 11.1685 14.2809 11.0479 14.5266 10.999C14.7722 10.9502 15.0268 10.9752 15.2582 11.0711C15.4896 11.1669 15.6874 11.3292 15.8265 11.5375C15.9657 11.7457 16.0399 11.9905 16.0399 12.241C16.0399 12.5768 15.9065 12.8989 15.669 13.1364C15.4316 13.3739 15.1095 13.5073 14.7736 13.5073ZM18.5725 6.75365H1.68841V3.37683H4.22103V4.22103C4.22103 4.44493 4.30998 4.65966 4.4683 4.81798C4.62662 4.9763 4.84134 5.06524 5.06524 5.06524C5.28914 5.06524 5.50387 4.9763 5.66219 4.81798C5.8205 4.65966 5.90945 4.44493 5.90945 4.22103V3.37683H14.3515V4.22103C14.3515 4.44493 14.4405 4.65966 14.5988 4.81798C14.7571 4.9763 14.9718 5.06524 15.1957 5.06524C15.4196 5.06524 15.6343 4.9763 15.7927 4.81798C15.951 4.65966 16.0399 4.44493 16.0399 4.22103V3.37683H18.5725V6.75365Z"
                        fill="#293050"
                      />
                    </svg>
                  }
                  onSelectionChange={keys => {
                    const value = Array.from(keys)[0] as
                      | "last30Days"
                      | "currentMonth"
                      | "lastMonth"
                      | "custom";
                    setExpenseRange(value);
                  }}
                >
                  {expensecFilter.map(item => (
                    <SelectItem key={item.value}>{item.label}</SelectItem>
                  ))}
                </NeoSelect>
                {showDatePicker && (
                  // <div className="w-56">
                  // {" "}
                  <NeoDatePicker
                    type={"range"}
                    label="Date Range"
                    rangedefaultValue={timeRange ? timeRange : undefined}
                    handleDateChange={onDateChange}
                  />
                  // </div>
                )}
              </div>

              {/* 2 col e */}
              {/* 3 col s */}
              <div className=" flex gap-10 p-2 ">
                {/* 11 */}
                <div className=" flex flex-col items-center justify-center gap-1">
                  {/* total ex */}
                  <div className=" flex flex-col items-center justify-center">
                    <h5 className="text-xl font-bold ">
                      ₹ {selectedData.total}
                    </h5>
                    <h5 className="text-sm font-normal text-gray-600">
                      Total Expenses
                    </h5>
                  </div>
                  <div>
                    <Chart
                      key={expenseSummaryChartKey}
                      options={expenseSummaryOptions}
                      series={expenseSummarySeries}
                      type="donut"
                      width="120"
                      height="120"
                    />
                  </div>
                  {/* total ex  */}
                </div>
                {/* 11 */}
                {/* 22 */}
                <div className=" flex flex-col gap-4">
                  {/* 1 */}
                  <div className="flex gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-orange-200">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 26 26"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.60751 3.21501C1.60751 1.44173 3.04924 0 4.82252 0H12.8601C14.6333 0 16.0751 1.44173 16.0751 3.21501V12.8601H16.4769C18.9183 12.8601 20.8976 14.8393 20.8976 17.2807V18.8882C20.8976 19.5563 21.4351 20.0938 22.1032 20.0938C22.7713 20.0938 23.3088 19.5563 23.3088 18.8882V11.1521C21.9224 10.7954 20.8976 9.53452 20.8976 8.03753V4.82252L19.2901 3.21501C18.848 2.77295 18.848 2.04957 19.2901 1.60751C19.7321 1.16544 20.4555 1.16544 20.8976 1.60751L24.7807 5.49064C25.3835 6.09345 25.7201 6.91228 25.7201 7.76627V8.43941V9.64504V11.2525V18.8882C25.7201 20.8875 24.1026 22.5051 22.1032 22.5051C20.1039 22.5051 18.4863 20.8875 18.4863 18.8882V17.2807C18.4863 16.1705 17.5871 15.2713 16.4769 15.2713H16.0751V22.5051C16.9642 22.5051 17.6826 23.2234 17.6826 24.1126C17.6826 25.0017 16.9642 25.7201 16.0751 25.7201H1.60751C0.718354 25.7201 0 25.0017 0 24.1126C0 23.2234 0.718354 22.5051 1.60751 22.5051V3.21501ZM4.82252 4.01877V8.84129C4.82252 9.28335 5.18421 9.64504 5.62627 9.64504H12.0563C12.4984 9.64504 12.8601 9.28335 12.8601 8.84129V4.01877C12.8601 3.5767 12.4984 3.21501 12.0563 3.21501H5.62627C5.18421 3.21501 4.82252 3.5767 4.82252 4.01877Z"
                          fill="#FF9F0A"
                        />
                      </svg>
                    </div>
                    <div>
                      <div className="font-bold">₹ {selectedData.fuel}</div>
                      <div className="text-xs text-gray-500">
                        Fuel- {fuelpercent}%
                      </div>
                    </div>
                  </div>
                  {/* 1 */}
                  {/* 2 */}
                  <div className="flex gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-200">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 26 26"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.6826 16.0751C22.1233 16.0751 25.7201 12.4783 25.7201 8.03753C25.7201 7.26894 25.6096 6.52547 25.4086 5.81716C25.2529 5.27463 24.5848 5.15407 24.188 5.55092L20.3299 9.40894C20.1792 9.55964 19.9733 9.64504 19.7623 9.64504H16.8788C16.4368 9.64504 16.0751 9.28335 16.0751 8.84129V5.95782C16.0751 5.74684 16.1605 5.54087 16.3112 5.39017L20.1692 1.53215C20.566 1.1353 20.4405 0.467182 19.9029 0.311454C19.1946 0.110516 18.4512 0 17.6826 0C13.2418 0 9.64504 3.5968 9.64504 8.03753C9.64504 8.99701 9.81584 9.92133 10.1223 10.7753L0.999668 19.8979C0.361689 20.5359 0 21.405 0 22.3092C0 24.193 1.52713 25.7201 3.41093 25.7201C4.31515 25.7201 5.18421 25.3584 5.82219 24.7204L14.9448 15.5978C15.7988 15.9093 16.7231 16.0751 17.6826 16.0751ZM4.01877 20.4957C4.33852 20.4957 4.64518 20.6227 4.87128 20.8488C5.09737 21.0749 5.2244 21.3816 5.2244 21.7013C5.2244 22.0211 5.09737 22.3277 4.87128 22.5538C4.64518 22.7799 4.33852 22.907 4.01877 22.907C3.69901 22.907 3.39236 22.7799 3.16626 22.5538C2.94016 22.3277 2.81314 22.0211 2.81314 21.7013C2.81314 21.3816 2.94016 21.0749 3.16626 20.8488C3.39236 20.6227 3.69901 20.4957 4.01877 20.4957Z"
                          fill="#0067FF"
                        />
                      </svg>
                    </div>
                    <div>
                      <div className="font-bold">
                        ₹ {selectedData.maintenance}
                      </div>
                      <div className="text-xs text-gray-500">
                        Maintenance-{maintenancepercent}%
                      </div>
                    </div>
                  </div>
                  {/* 2 */}
                  {/* 3 */}
                  <div className="flex gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-green-200">
                      <svg
                        width="20"
                        height="18"
                        viewBox="0 0 29 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.77892 0H26.6839C27.6678 0 28.4628 0.794956 28.4628 1.77892V3.55785C28.4628 4.54181 27.6678 5.33677 26.6839 5.33677H1.77892C0.794956 5.33677 0 4.54181 0 3.55785V1.77892C0 0.794956 0.794956 0 1.77892 0ZM1.77892 7.11569H26.6839V21.3471C26.6839 23.3095 25.0884 24.9049 23.126 24.9049H5.33677C3.3744 24.9049 1.77892 23.3095 1.77892 21.3471V7.11569ZM8.89462 11.563C8.89462 12.0522 9.29488 12.4525 9.78408 12.4525H18.6787C19.1679 12.4525 19.5682 12.0522 19.5682 11.563C19.5682 11.0738 19.1679 10.6735 18.6787 10.6735H9.78408C9.29488 10.6735 8.89462 11.0738 8.89462 11.563Z"
                          fill="#349C42"
                        />
                      </svg>
                    </div>
                    <div>
                      <div className="font-bold">
                        ₹ {selectedData.miscellaneous}
                      </div>
                      <div className="text-xs text-gray-500">
                        Miscellaneous-{miscpercent}%
                      </div>
                    </div>
                  </div>
                  {/* 3 */}
                </div>
                {/* 22 */}
              </div>
            </>
          )}
          {/* 3 col e */}
        </div>
      </div>
      {/* main */}
    </>
  );
};
export default ExpenseSummaryWidget;
